/**
 * File validation utilities for secure file uploads
 * Provides client-side validation as first line of defense
 */

/**
 * Allowed MIME types for file uploads
 * This whitelist prevents potentially dangerous file types
 */
export const ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  // Documents
  'application/pdf',
  'text/plain',
  'text/csv',
  // Microsoft Office
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Audio/Video
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
  'video/mp4',
  'video/webm',
  // Archives (optional - can be disabled)
  'application/zip',
  'application/x-rar-compressed',
]);

/**
 * Dangerous file extensions that should always be blocked
 * Even if MIME type looks safe, these extensions are risky
 */
export const BLOCKED_EXTENSIONS: ReadonlySet<string> = new Set([
  '.exe', '.dll', '.bat', '.cmd', '.sh', '.bash',
  '.ps1', '.psm1', '.psd1',  // PowerShell
  '.vbs', '.vbe', '.js', '.jse', '.ws', '.wsf', '.wsc', '.wsh',  // Scripts
  '.msi', '.msp', '.mst',  // Windows installers
  '.scr', '.pif', '.com',  // Executables
  '.hta', '.cpl', '.msc',  // Control panel/settings
  '.jar', '.class',  // Java
  '.php', '.php3', '.php4', '.php5', '.phtml',  // PHP
  '.asp', '.aspx', '.ashx', '.asmx',  // ASP.NET
  '.jsp', '.jspx',  // JSP
  '.py', '.pyc', '.pyw',  // Python
  '.rb', '.rbw',  // Ruby
  '.pl', '.pm', '.cgi',  // Perl/CGI
  '.htaccess', '.htpasswd',  // Apache config
]);

/**
 * Default file upload constraints
 */
export const DEFAULT_FILE_CONSTRAINTS = {
  maxSizeBytes: 25 * 1024 * 1024,  // 25MB
  minSizeBytes: 1,  // At least 1 byte
  maxFilenameLength: 255,
} as const;

/**
 * File validation configuration
 */
export interface FileValidationConfig {
  maxSizeBytes?: number;
  minSizeBytes?: number;
  allowedMimeTypes?: ReadonlySet<string> | Set<string>;
  blockedExtensions?: ReadonlySet<string> | Set<string>;
  maxFilenameLength?: number;
}

/**
 * File validation result
 */
export interface FileValidationResult {
  valid: boolean;
  error?: string;
  errorCode?: 'FILE_TOO_LARGE' | 'FILE_TOO_SMALL' | 'INVALID_TYPE' | 'BLOCKED_EXTENSION' | 'FILENAME_TOO_LONG' | 'EMPTY_FILE';
}

/**
 * Get file extension from filename (lowercase)
 */
export function getFileExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) {
    return '';
  }
  return filename.substring(lastDot).toLowerCase();
}

/**
 * Sanitize filename for safe storage
 * Removes path traversal attempts and dangerous characters
 */
export function sanitizeFilename(filename: string): string {
  // Remove path components
  let sanitized = filename.replace(/^.*[\\/]/, '');

  // Remove null bytes and other control characters
  sanitized = sanitized.replace(/[\x00-\x1f\x7f]/g, '');

  // Replace problematic characters
  sanitized = sanitized.replace(/[<>:"/\\|?*]/g, '_');

  // Trim whitespace and dots from start/end
  sanitized = sanitized.replace(/^[\s.]+|[\s.]+$/g, '');

  // If empty after sanitization, use default name
  if (!sanitized) {
    sanitized = 'unnamed_file';
  }

  // Limit length
  if (sanitized.length > DEFAULT_FILE_CONSTRAINTS.maxFilenameLength) {
    const ext = getFileExtension(sanitized);
    const nameWithoutExt = sanitized.slice(0, -ext.length || undefined);
    const maxNameLength = DEFAULT_FILE_CONSTRAINTS.maxFilenameLength - ext.length;
    sanitized = nameWithoutExt.slice(0, maxNameLength) + ext;
  }

  return sanitized;
}

/**
 * Validate a file before upload
 * Returns validation result with error details if invalid
 */
export function validateFile(
  file: File,
  config: FileValidationConfig = {}
): FileValidationResult {
  const {
    maxSizeBytes = DEFAULT_FILE_CONSTRAINTS.maxSizeBytes,
    minSizeBytes = DEFAULT_FILE_CONSTRAINTS.minSizeBytes,
    allowedMimeTypes = ALLOWED_MIME_TYPES,
    blockedExtensions = BLOCKED_EXTENSIONS,
    maxFilenameLength = DEFAULT_FILE_CONSTRAINTS.maxFilenameLength,
  } = config;

  // Check file size
  if (file.size === 0) {
    return {
      valid: false,
      error: 'File is empty',
      errorCode: 'EMPTY_FILE',
    };
  }

  if (file.size < minSizeBytes) {
    return {
      valid: false,
      error: `File is too small. Minimum size is ${formatFileSize(minSizeBytes)}`,
      errorCode: 'FILE_TOO_SMALL',
    };
  }

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `File is too large. Maximum size is ${formatFileSize(maxSizeBytes)}`,
      errorCode: 'FILE_TOO_LARGE',
    };
  }

  // Check filename length
  if (file.name.length > maxFilenameLength) {
    return {
      valid: false,
      error: `Filename is too long. Maximum length is ${maxFilenameLength} characters`,
      errorCode: 'FILENAME_TOO_LONG',
    };
  }

  // Check for blocked extensions
  const extension = getFileExtension(file.name);
  if (extension && blockedExtensions.has(extension)) {
    return {
      valid: false,
      error: `File type "${extension}" is not allowed for security reasons`,
      errorCode: 'BLOCKED_EXTENSION',
    };
  }

  // Check MIME type
  const mimeType = file.type || 'application/octet-stream';
  if (!allowedMimeTypes.has(mimeType)) {
    // Allow files without explicit type if extension is safe
    if (mimeType === 'application/octet-stream' && extension && !blockedExtensions.has(extension)) {
      // Let it pass - backend will verify
    } else {
      return {
        valid: false,
        error: `File type "${mimeType}" is not allowed. Allowed types: images, documents, audio, video`,
        errorCode: 'INVALID_TYPE',
      };
    }
  }

  return { valid: true };
}

/**
 * Format file size to human-readable string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Generate accept attribute string from ALLOWED_MIME_TYPES
 * Used to sync file input accept with validation rules
 */
export function generateAcceptAttribute(allowedMimeTypes: ReadonlySet<string> = ALLOWED_MIME_TYPES): string {
  const accepts: string[] = [];

  allowedMimeTypes.forEach((mimeType) => {
    accepts.push(mimeType);
  });

  return accepts.join(',');
}

/**
 * Rate limiter for file uploads
 * Prevents rapid successive uploads
 */
export class UploadRateLimiter {
  private uploadTimestamps: number[] = [];
  private readonly maxUploads: number;
  private readonly windowMs: number;

  constructor(maxUploads = 5, windowMs = 60000) {
    this.maxUploads = maxUploads;
    this.windowMs = windowMs;
  }

  /**
   * Check if upload is allowed
   * Returns true if allowed, false if rate limited
   */
  canUpload(): boolean {
    const now = Date.now();
    // Remove timestamps outside the window
    this.uploadTimestamps = this.uploadTimestamps.filter(
      (timestamp) => now - timestamp < this.windowMs
    );
    return this.uploadTimestamps.length < this.maxUploads;
  }

  /**
   * Record an upload attempt
   */
  recordUpload(): void {
    this.uploadTimestamps.push(Date.now());
  }

  /**
   * Get remaining uploads in current window
   */
  getRemainingUploads(): number {
    const now = Date.now();
    this.uploadTimestamps = this.uploadTimestamps.filter(
      (timestamp) => now - timestamp < this.windowMs
    );
    return Math.max(0, this.maxUploads - this.uploadTimestamps.length);
  }

  /**
   * Get time until next upload is allowed (in ms)
   * Returns 0 if upload is allowed now
   */
  getTimeUntilNextUpload(): number {
    if (this.canUpload()) return 0;
    const oldestTimestamp = Math.min(...this.uploadTimestamps);
    return Math.max(0, this.windowMs - (Date.now() - oldestTimestamp));
  }

  /**
   * Reset the rate limiter
   */
  reset(): void {
    this.uploadTimestamps = [];
  }
}
